// ─────────────────────────────────────────────────────────────────────────────
// MASTER CART INTERCEPTOR  (Bundle + Promo)
// Load blocking in <head> — no defer, no async
// ─────────────────────────────────────────────────────────────────────────────

(function () {
  if (window.Shopify?.designMode) return;

  // ── Early-load guard ───────────────────────────────────────────────────────
  // Applied immediately so even a click that fires before DOMContentLoaded
  // is blocked. Removed once the interceptor is live.
  const READY_CLASS  = 'ts-interceptor-ready';
  const PENDING_ATTR = 'data-ts-interceptor-pending';

  function disableBundleButtons() {
    document.querySelectorAll('.ts-bundle-wrapper').forEach(wrapper => {
      // The ATC button lives outside the wrapper in Dawn's product-form,
      // identified by the section id embedded in its id attribute.
      // Fallback: disable every submit in the nearest product-form.
      const form = wrapper.closest('product-form, form[action*="/cart/add"]');
      if (!form) return;
      form.querySelectorAll('[type="submit"]').forEach(btn => {
        if (btn.hasAttribute(PENDING_ATTR)) return;
        btn.setAttribute(PENDING_ATTR, 'true');
        btn.disabled = true;
      });
    });
  }

  function enableBundleButtons() {
    document.querySelectorAll(`[${PENDING_ATTR}]`).forEach(btn => {
      btn.removeAttribute(PENDING_ATTR);
      btn.disabled = false;
    });
    document.body.classList.add(READY_CLASS);
  }

  // Disable as early as possible — if DOM is already parsed hit it now,
  // otherwise wait for it.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', disableBundleButtons, { once: true });
  } else {
    disableBundleButtons();
  }

  // CSS belt-and-suspenders: pointer-events off until ready
  // (catches anything disableBundleButtons can't reach yet)
  const styleEl = document.createElement('style');
  styleEl.textContent =
    'body:not(.' + READY_CLASS + ') .ts-bundle-wrapper ~ * [type="submit"],' +
    'body:not(.' + READY_CLASS + ') .ts-bundle-wrapper [type="submit"] { pointer-events: none !important; }';
  document.head.appendChild(styleEl);

  // ── Interceptor ───────────────────────────────────────────────────────────
  const originalFetch    = window.fetch;
  let   isMasterProcessing = false;

  window.fetch = async function (...args) {
    const url     = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
    const options = args[1] || {};

    if (!url.includes('/cart/add') || isMasterProcessing) {
      return originalFetch.apply(this, args);
    }

    isMasterProcessing = true;

    try {
      // ── 1. Parse what Dawn is trying to send ──────────────────────────────
      let addingVariantId   = null;
      let addingQuantity    = 1;
      let requestedSections = [];   // always normalised to array
      let sectionsUrl       = '';
      let originalItems     = [];

      const body = options.body;

      if (body instanceof FormData) {
        addingVariantId   = body.get('id');
        addingQuantity    = body.get('quantity') || 1;
        // FormData sends sections as a comma-separated string
        const raw         = body.get('sections') || '';
        requestedSections = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
        sectionsUrl       = body.get('sections_url') || '';

      } else if (body instanceof URLSearchParams) {
        addingVariantId   = body.get('id');
        addingQuantity    = body.get('quantity') || 1;
        const raw         = body.get('sections') || '';
        requestedSections = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
        sectionsUrl       = body.get('sections_url') || '';

      } else if (typeof body === 'string' && body.trim()) {
        try {
          const parsed = JSON.parse(body);
          if (parsed.items && Array.isArray(parsed.items)) {
            originalItems   = parsed.items;
            addingVariantId = String(parsed.items[0]?.id || '');
            addingQuantity  = parsed.items[0]?.quantity || 1;
          } else {
            addingVariantId = String(parsed.id || '');
            addingQuantity  = parsed.quantity || 1;
          }
          // JSON body may send sections as array or comma string — normalise both
          const rawSections = parsed.sections;
          if (Array.isArray(rawSections)) {
            requestedSections = rawSections;
          } else if (typeof rawSections === 'string' && rawSections) {
            requestedSections = rawSections.split(',').map(s => s.trim()).filter(Boolean);
          }
          sectionsUrl = parsed.sections_url || '';
        } catch {
          const params      = new URLSearchParams(body);
          addingVariantId   = params.get('id');
          addingQuantity    = params.get('quantity') || 1;
          const raw         = params.get('sections') || '';
          requestedSections = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
          sectionsUrl       = params.get('sections_url') || '';
        }
      }

      addingVariantId = String(addingVariantId || '').trim();
      const parsedVariantId = parseInt(addingVariantId, 10);
      if (!parsedVariantId || isNaN(parsedVariantId)) {
        console.warn('Master Interceptor: could not resolve variant ID, passing through');
        return originalFetch.apply(this, args);
      }

      // ── 2. Check which features are active ────────────────────────────────
      const bundleWrapper = document.querySelector(
        `.ts-bundle-wrapper[data-bundle-variant="${addingVariantId}"]`
      );
      const promoCheckbox = document.querySelector('.ts-promo-checkbox');

      const isBundleActive = !!bundleWrapper;
      const isPromoActive  = !!(promoCheckbox?.dataset.variantId);

      if (!isBundleActive && !isPromoActive) {
        return originalFetch.apply(this, args);
      }

      // ── 3. Build final items array ─────────────────────────────────────────
      let finalItemsToAdd = [];

      if (isBundleActive) {
        try {
          const bundleItems = JSON.parse(bundleWrapper.dataset.bundleItems || '[]');
          if (!bundleItems.length) throw new Error('bundleItems is empty');

          bundleItems.forEach(item => {
            const id  = parseInt(item.variant_id, 10);
            const qty = parseInt(item.qty, 10) || 1;
            if (!id || isNaN(id)) {
              console.warn('Master Interceptor: skipping invalid bundle item', item);
              return;
            }
            finalItemsToAdd.push({ id, quantity: qty });
          });

          if (!finalItemsToAdd.length) throw new Error('no valid bundle items after filter');

        } catch (e) {
          console.error('Master Interceptor: bundle parse failed, falling back', e);
          finalItemsToAdd = [{ id: parsedVariantId, quantity: parseInt(addingQuantity, 10) || 1 }];
        }

      } else if (originalItems.length > 0) {
        finalItemsToAdd = originalItems.map(item => ({
          id:       parseInt(item.id, 10),
          quantity: parseInt(item.quantity, 10) || 1,
          ...(item.properties   && { properties:   item.properties }),
          ...(item.selling_plan && { selling_plan: item.selling_plan })
        }));

      } else {
        finalItemsToAdd = [{ id: parsedVariantId, quantity: parseInt(addingQuantity, 10) || 1 }];
      }

      // ── 3B. Promo item ─────────────────────────────────────────────────────
      if (isPromoActive) {
        const promoId = parseInt(promoCheckbox.dataset.variantId, 10);
        if (promoId && !isNaN(promoId)) {
          const alreadyInBatch = finalItemsToAdd.some(item => item.id === promoId);
          if (!alreadyInBatch) {
            let alreadyInCart = false;
            try {
              const cartRes  = await originalFetch(window.Shopify.routes.root + 'cart.js');
              const cartData = await cartRes.json();
              alreadyInCart  = cartData.items.some(i => i.variant_id === promoId);
            } catch (e) {
              console.warn('Master Interceptor: cart check for promo dedup failed', e);
            }
            if (!alreadyInCart) {
              finalItemsToAdd.push({ id: promoId, quantity: 1 });
            }
          }
        }
      }

      // ── 4. Add the real items first ────────────────────────────────────────
      // IMPORTANT: add before removing placeholder.
      // If the add fails, the placeholder stays — no silent cart wipe.
      const payload = { items: finalItemsToAdd };
      if (requestedSections.length) payload.sections     = requestedSections;
      if (sectionsUrl)              payload.sections_url = sectionsUrl;

      const addResponse = await originalFetch(
        window.Shopify.routes.root + 'cart/add.js',
        {
          method:  'POST',
          headers: {
            'Content-Type':     'application/json',
            'X-Requested-With': 'xmlhttprequest'
          },
          body: JSON.stringify(payload)
        }
      );

      // ── 5. Remove placeholder ONLY after a successful add ─────────────────
      if (isBundleActive && addResponse.ok) {
        try {
          const cartRes  = await originalFetch(window.Shopify.routes.root + 'cart.js');
          const cartData = await cartRes.json();
          const placeholder = cartData.items.find(
            i => String(i.variant_id) === addingVariantId
          );
          if (placeholder) {
            await originalFetch(window.Shopify.routes.root + 'cart/change.js', {
              method:  'POST',
              headers: { 'Content-Type': 'application/json' },
              body:    JSON.stringify({ id: placeholder.key, quantity: 0 })
            });
          }
        } catch (e) {
          console.warn('Master Interceptor: placeholder removal failed', e);
        }
      }

      return addResponse;

    } catch (error) {
      console.error('Master Interceptor: unhandled error, passing through', error);
      return originalFetch.apply(this, args);
    } finally {
      isMasterProcessing = false;
    }
  };

  // ── Mark ready ─────────────────────────────────────────────────────────────
  // Re-enable any buttons that were locked during load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableBundleButtons, { once: true });
  } else {
    enableBundleButtons();
  }

})();